import React from 'react';
import { Skeleton } from '@/components/ui/Skeleton';
import { AtividadeItem } from '@/hooks/useDashboard';

interface Props {
  items: AtividadeItem[];
  loading?: boolean;
}

// Paleta para avatares não-IA, selecionada por hash do nome
const AVATAR_PALETTE = [
  'bg-info dark:bg-info-dark',
  'bg-success dark:bg-success-dark',
  'bg-warning dark:bg-warning-dark',
  'bg-fuchsia-500 dark:bg-fuchsia-400',
];

function hashToIdx(s: string, n: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % n;
}

function timeAgo(d: Date): string {
  const diff = Math.max(0, Date.now() - d.getTime());
  const mins = Math.round(diff / 60_000);
  if (mins < 1) return 'agora';
  if (mins < 60) return `há ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  const weeks = Math.round(days / 7);
  if (weeks < 4) return `há ${weeks} sem`;
  const months = Math.round(days / 30);
  return `há ${months} mês${months > 1 ? 'es' : ''}`;
}

export function AtividadeRecenteCard({ items, loading }: Props) {
  return (
    <section
      aria-labelledby="atividade-titulo"
      className="bg-surface dark:bg-surface-dark border border-line dark:border-line-dark rounded-card shadow-sm-warm dark:shadow-sm-dark overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-[18px] pt-4 pb-3 border-b border-line dark:border-line-dark">
        <h3 id="atividade-titulo" className="text-[14px] font-semibold text-ink-title dark:text-ink-title-dark">
          Atividade recente
        </h3>
      </div>

      {/* Body */}
      <ul className="flex flex-col flex-1">
        {loading ? (
          [1, 2, 3, 4, 5].map(i => (
            <li key={i} className="flex items-center gap-3 px-[18px] py-2.5">
              <Skeleton className="h-7 w-7" circle />
              <div className="flex-1 min-w-0">
                <Skeleton className="h-3 w-3/4" />
              </div>
              <Skeleton className="h-3 w-14" />
            </li>
          ))
        ) : items.length === 0 ? (
          <li className="py-8 text-center text-[13px] text-ink-subtitle dark:text-ink-subtitle-dark">
            Nenhuma atividade recente.
          </li>
        ) : (
          items.map(item => {
            const avatarColor = item.isAI
              ? 'bg-brand-500'
              : AVATAR_PALETTE[hashToIdx(item.userName, AVATAR_PALETTE.length)];
            return (
              <li key={item.id} className="flex items-center gap-3 px-[18px] py-2.5 hover:bg-surface-alt dark:hover:bg-surface-dark-alt transition-colors">
                <div
                  className={[
                    'w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0',
                    avatarColor,
                  ].join(' ')}
                  aria-hidden
                >
                  {item.userInitials}
                </div>
                <div className="flex-1 min-w-0 text-[12.5px] leading-tight">
                  <span className="text-ink-subtitle dark:text-ink-subtitle-dark">{item.action} </span>
                  {item.target && (
                    <span className="text-ink-title dark:text-ink-title-dark font-medium">
                      {item.target}
                    </span>
                  )}
                </div>
                <time
                  dateTime={item.createdAt.toISOString()}
                  className="text-[11px] text-ink-subtitle dark:text-ink-subtitle-dark flex-shrink-0 tabular"
                >
                  {timeAgo(item.createdAt)}
                </time>
              </li>
            );
          })
        )}
      </ul>
    </section>
  );
}
